import { z } from 'zod';

/** Trimmed non-empty string (after trim). */
export const nonEmptyString = (message = 'Required') =>
  z.string().trim().min(1, message);

/** Optional string that becomes undefined when empty after trim. */
export const optionalTrimmedString = z
  .string()
  .optional()
  .transform((s) => {
    if (s == null) return undefined;
    const t = s.trim();
    return t === '' ? undefined : t;
  });

/** FHIR Reference shape: `{ reference: "ResourceType/id", display?: string }` */
export const fhirReferenceSchema = z.object({
  reference: z.string().min(1),
  display: z.string().optional(),
});

/** FHIR Coding: `{ system, code, display? }` */
export const fhirCodingSchema = z.object({
  system: z.string().optional(),
  code: z.string().min(1),
  display: z.string().optional(),
});

/** FHIR CodeableConcept: `{ coding[], text? }` */
export const fhirCodeableConceptSchema = z.object({
  coding: z.array(fhirCodingSchema).optional(),
  text: z.string().optional(),
});

/** FHIR Extension: `{ url, value[x] }` — loose shape for generic extension validation. */
export const fhirExtensionSchema = z.object({
  url: z.string().min(1),
  valueString: z.string().optional(),
  valueBoolean: z.boolean().optional(),
  valueCode: z.string().optional(),
  valueUrl: z.string().optional(),
  valueReference: fhirReferenceSchema.optional(),
  valueDateTime: z.string().optional(),
  valueInteger: z.number().optional(),
});

/** FHIR Period: `{ start?, end? }` */
export const fhirPeriodSchema = z.object({
  start: z.string().optional(),
  end: z.string().optional(),
});

/** FHIR HumanName: `{ given[], family, prefix[], use? }` */
export const fhirHumanNameSchema = z.object({
  use: z.enum(['official', 'usual', 'temp', 'nickname', 'anonymous', 'old', 'maiden']).optional(),
  family: z.string().optional(),
  given: z.array(z.string()).optional(),
  prefix: z.array(z.string()).optional(),
  suffix: z.array(z.string()).optional(),
});

/** FHIR ContactPoint: `{ system, value, use? }` */
export const fhirContactPointSchema = z.object({
  system: z.enum(['phone', 'fax', 'email', 'pager', 'url', 'sms', 'other']).optional(),
  value: z.string().optional(),
  use: z.enum(['home', 'work', 'temp', 'old', 'mobile']).optional(),
});

/** FHIR Address: `{ line[], city, state, postalCode, country }` */
export const fhirAddressSchema = z.object({
  use: z.enum(['home', 'work', 'temp', 'old', 'billing']).optional(),
  line: z.array(z.string()).optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postalCode: z.string().optional(),
  country: z.string().optional(),
});

/** FHIR Identifier: `{ system, value }` */
export const fhirIdentifierSchema = z.object({
  system: z.string().optional(),
  value: z.string().min(1),
});

/** FHIR meta.tag entry */
export const fhirMetaTagSchema = z.object({
  system: z.string().optional(),
  code: z.string().min(1),
  display: z.string().optional(),
});

/** ISO date string (YYYY-MM-DD). */
export const isoDateString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD');

/** ISO datetime string (accepts full ISO 8601 with optional time). */
export const isoDateTimeString = z.string().min(10);
